import { type FC, useMemo, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { sentryUtils } from "@/shared/utils/sentry";
import { currencyInput } from "@/shared/utils/currencyInput";
import { FieldError } from "@/shared/components/ui/FieldError";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { NONE_VALUE } from "./OngoingCostsChecklist";

const PROPERTY_COST_TYPES = ["HOA", "Utilities", "Homeowners insurance", "Property tax"];

interface CostTypeConfig {
  title: string;
  subtitle: string;
  itemType: string;
}

const COST_TYPE_CONFIG: Record<string, CostTypeConfig> = {
  ongoing_property_costs: {
    title: "Add a property cost",
    subtitle:
      "These are recurring bills that keep going during settlement. We'll track them so they can be reimbursed from the estate.",
    itemType: "Ongoing property costs",
  },
  storage_units: {
    title: "Add a storage unit",
    subtitle:
      "We'll track the monthly cost so it can be reimbursed from the estate. You can also note when it's time to close it out.",
    itemType: "Storage units",
  },
  subscriptions: {
    title: "Add a subscription",
    subtitle:
      "We'll track ongoing charges so they can be reimbursed, and help you decide what to cancel or keep.",
    itemType: "Subscriptions",
  },
};

interface OngoingCostFormValues {
  costType: string;
  name: string;
  address: string;
  unitNumber: string;
  amount: string;
  documentFile: File | null;
}

const EMPTY_FORM: OngoingCostFormValues = {
  costType: "",
  name: "",
  address: "",
  unitNumber: "",
  amount: "",
  documentFile: null,
};

interface AddOngoingCostProps extends FlowCustomComponentProps {
  costKey: string;
}

export const AddOngoingCost: FC<AddOngoingCostProps> = ({ costKey, goToScreen, answers }) => {
  const config = COST_TYPE_CONFIG[costKey];
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    OngoingCostFormValues,
    { name?: string; costType?: string }
  >(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountInput = currencyInput(form.amount, (val) => updateForm({ ...form, amount: val }));

  const rawSelected = answers["ongoing_costs_selected"];
  const selected: string[] = useMemo(() => {
    return Array.isArray(rawSelected) ? rawSelected.filter((v) => v !== NONE_VALUE) : [];
  }, [rawSelected]);

  const currentIndex = selected.indexOf(costKey);
  const nextKey = currentIndex < selected.length - 1 ? selected[currentIndex + 1] : null;

  const advance = (updatedAnswers: Record<string, string | string[]>) => {
    if (nextKey) {
      goToScreen(`add_ongoing_cost_${nextKey}`, updatedAnswers);
    } else {
      goToScreen("ongoing_costs_checklist", updatedAnswers);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || isAddingItem || !estateId || !config) return;

    if (costKey === "ongoing_property_costs" && !form.costType) {
      setFieldErrors({ costType: "Please select a cost type" });
      return;
    }
    if (!form.name.trim()) {
      const label =
        costKey === "ongoing_property_costs"
          ? "vendor or payee"
          : costKey === "storage_units"
            ? "storage facility"
            : "service name";
      setFieldErrors({ name: `Please enter the ${label}` });
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;
      if (form.documentFile) {
        sourceDocumentId = await uploadDocumentWithAuth(
          form.documentFile,
          estateId,
          "expense_receipt",
        );
      }

      const metadata: Record<string, unknown> = {};
      if (form.costType) metadata.cost_type = form.costType;
      if (form.address.trim()) metadata.address = form.address.trim();
      if (form.unitNumber.trim()) metadata.unit_number = form.unitNumber.trim();

      await addItem({
        estate_id: estateId,
        category: "Ongoing Estate Expenses",
        classification: "debt",
        item_type: config.itemType,
        item_name: form.name.trim(),
        balance: parseFloat(form.amount) || 0,
        metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        source_document_id: sourceDocumentId,
      });

      advance({ ...answers, [`ongoing_cost_${costKey}_done`]: "yes" });
    } catch (err) {
      setError("Failed to add expense. Please try again.");
      sentryUtils.captureError(err, "AddOngoingCost submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!config) return null;

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">{config.title}</h2>
      <p className="mt-2 text-center text-secondary">{config.subtitle}</p>
      <div className="mt-8 w-full">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting || isAddingItem}
          error={error}
          onSkip={() => advance({ ...answers, [`ongoing_cost_${costKey}_done`]: "skipped" })}
          skipLabel="Skip for now"
        >
          {costKey === "ongoing_property_costs" && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label required>Type of cost</Label>
                <Select
                  value={form.costType}
                  onValueChange={(val) => updateForm({ ...form, costType: val })}
                >
                  <SelectTrigger className={fieldErrors.costType ? "border-error" : ""}>
                    <SelectValue placeholder="Select cost type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_COST_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={fieldErrors.costType} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label required>Vendor or payee</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => updateForm({ ...form, name: e.target.value })}
                    placeholder="e.g., National Grid"
                    className={fieldErrors.name ? "border-error" : ""}
                  />
                  <FieldError message={fieldErrors.name} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Monthly amount</Label>
                  <CurrencyInput
                    value={amountInput.displayValue}
                    onChange={amountInput.handleChange}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <UploadZone
                label="Recent bill"
                labelSuffix="(optional)"
                file={form.documentFile}
                onFileSelect={(file) => updateForm({ ...form, documentFile: file })}
                onFileRemove={() => updateForm({ ...form, documentFile: null })}
              />
            </div>
          )}

          {costKey === "storage_units" && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label required>Storage facility</Label>
                <Input
                  value={form.name}
                  onChange={(e) => updateForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Public Storage, Extra Space"
                  className={fieldErrors.name ? "border-error" : ""}
                />
                <FieldError message={fieldErrors.name} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => updateForm({ ...form, address: e.target.value })}
                  placeholder="Street, city, state, zip"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>Monthly cost</Label>
                  <CurrencyInput
                    value={amountInput.displayValue}
                    onChange={amountInput.handleChange}
                    placeholder="0.00"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>
                    Unit number <span className="text-tertiary">(optional)</span>
                  </Label>
                  <Input
                    value={form.unitNumber}
                    onChange={(e) => updateForm({ ...form, unitNumber: e.target.value })}
                    placeholder="e.g., Smith Funeral Home"
                  />
                </div>
              </div>
              <UploadZone
                label="Recent bill"
                labelSuffix="(optional)"
                file={form.documentFile}
                onFileSelect={(file) => updateForm({ ...form, documentFile: file })}
                onFileRemove={() => updateForm({ ...form, documentFile: null })}
              />
            </div>
          )}

          {costKey === "subscriptions" && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label required>Service name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => updateForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Netflix, Spotify, gym"
                    className={fieldErrors.name ? "border-error" : ""}
                  />
                  <FieldError message={fieldErrors.name} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Monthly cost</Label>
                  <CurrencyInput
                    value={amountInput.displayValue}
                    onChange={amountInput.handleChange}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <UploadZone
                label="Paid invoice"
                labelSuffix="(optional)"
                file={form.documentFile}
                onFileSelect={(file) => updateForm({ ...form, documentFile: file })}
                onFileRemove={() => updateForm({ ...form, documentFile: null })}
              />
            </div>
          )}
        </AddItemLayout>
      </div>
    </div>
  );
};

export { COST_TYPE_CONFIG };
